import React, { useState } from 'react'

import { Collapse, CircularProgress, Divider, Stack, Typography, useTheme } from '@mui/material'
import { ChevronDownIcon, RadioReceiverIcon, SmartphoneNfcIcon } from 'lucide-react'

import { Modal, Button } from '@repo/ui'
import { useScanRouting, type ScanRoutingTarget } from 'contexts'
import { ReaderSetupPanel } from 'components/display/ReaderSetupPanel/ReaderSetupPanel'
// The §34 scan-source surface, shared so this hand-roll and `ScanSessionBlock`'s cannot drift from
// the canonical `ScanSourceRow` treatment before VRG-322 converges them onto the component itself.
import { scanSourceSurfaceSx } from 'components/display/ScanSourceRow/ScanSourceRow'

import { ScanSessionQRCode } from '../ScanSessionBlock'

/**
 * Both source rows are pinned to one height so neither moves when its right-hand side changes
 * — "Not connected" becoming "Connected", or the reader row gaining a chevron. Same number, and
 * for the same reason, as `SOURCE_ROW_HEIGHT` in ScanSessionBlock.
 */
const SOURCE_ROW_HEIGHT = 42

/**
 * One sentence naming where the next scan lands, branched on the target's KIND.
 *
 * The two kinds are not the same sentence with a different noun in it. A filter field
 * accumulates scans as filter values; the toolbar search box holds one string, and it is not
 * a filter at all. Templating a single sentence off `target.label` — which is what this was —
 * produced "added to the Search filter" the moment the search box was aimed: it named a
 * control that does not exist and described the wrong behaviour. Compare `kind`, never the
 * spelling of the label (see `ScanRoutingTargetKind`).
 */
const whereScansLand = (target: ScanRoutingTarget): string =>
  target.kind === 'search'
    ? 'Scan a tag and its ID goes into the search box.'
    : `Scan a tag and it will be added to the ${target.label} filter.`

/**
 * Pair a scanner. That is the whole job.
 *
 * WHAT THIS REPLACED, AND WHY. The previous surface was a modal that held its own list of
 * resolved entities: you scanned into it, it showed what it had found, you pressed Apply and it
 * handed the set to the page. Two lists then existed for one idea — the modal's, and the filter's
 * — and the operator had to hold both in their head. The corrected model routes scans into the
 * filter dropdowns that already exist, so the modal has nothing left to list.
 *
 * What remains is a connection step, and a connection step should not outlive the connection: the
 * modal CLOSES the moment a phone registers (`ScanRoutingProvider` clears `connectOpen` on
 * `deviceRegistered`), leaving the operator looking at the filters their scans are about to fill
 * in rather than at a QR code they have already used.
 *
 * With a desk reader connected this modal is never needed at all — aiming a field opens the
 * session silently. It is reachable anyway, because a reader and a phone are additive input
 * sources and an operator with a reader may still want to walk away from the desk.
 */
export const ScanConnectModal = () => {
  const theme = useTheme()
  const {
    connectOpen,
    closeConnect,
    registerDeviceURL,
    phoneConnected,
    readerConnected,
    starting,
    target,
  } = useScanRouting()
  // Setup guidance the OPERATOR asks for. Closed by default — see the row's comment below.
  const [readerHelpOpen, setReaderHelpOpen] = useState(false)

  return (
    <Modal
      title="Connect a scanner"
      open={connectOpen}
      onClose={closeConnect}
      actions={
        <Button disableElevation variant="secondary" onClick={closeConnect}>
          Done
        </Button>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {target
            ? `${whereScansLand(target)} This closes as soon as your phone connects, and the session stays open while you work.`
            : 'Pair a phone, then open Filters and click the scan icon on the field you want scans to land in.'}
        </Typography>

        {/* "Desk reader — Not connected" USED TO END THERE: a fact, and nothing to do about it.
            The Create Certificates block already answers the same question, so the answer is now
            a shared component (§36) behind a "Learn more" accordion on this row.

            CLOSED BY DEFAULT, deliberately. ScanSessionBlock's copy of this panel was once forced
            open whenever no reader was connected, which spent ~260px of modal on setup for
            hardware the operator may not own; that was undone on purpose and must not come back
            here. Note the panel is offered even when a reader IS connected — the row keeps the
            same control and the same height either way, so nothing shifts when one appears. */}
        <Stack sx={{ px: 4, ...scanSourceSurfaceSx(theme, { interactive: true }) }}>
          {/* The whole row is the trigger — a 42px bar with one purpose beats a small text
              hit-target. "Learn more" is therefore plain text, not a nested button: a button
              inside a button double-fires the toggle on every click. */}
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            role="button"
            tabIndex={0}
            aria-expanded={readerHelpOpen}
            onClick={() => setReaderHelpOpen(v => !v)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setReaderHelpOpen(v => !v)
              }
            }}
            // `outline: none` because the hover/focus treatment is on the CONTAINER (it owns the
            // border a §34 tint sits inside) while the `tabIndex` is here — without this the UA's
            // own focus ring draws on top of the container's deliberate one. This row previously
            // had NO focus treatment at all, so a keyboard user had nothing telling them which
            // row Enter/Space was about to toggle.
            sx={{ minHeight: SOURCE_ROW_HEIGHT, cursor: 'pointer', userSelect: 'none', outline: 'none' }}
          >
            <RadioReceiverIcon
              strokeWidth={1}
              size={20}
              color={readerConnected ? theme.palette.brand[500] : theme.palette.grey[400]}
            />
            <Typography variant="body2" sx={{ flex: 1 }}>
              Desk reader
            </Typography>
            <Typography
              variant="body3"
              color={readerConnected ? theme.palette.brand[500] : 'text.secondary'}
            >
              {readerConnected ? 'Connected' : 'Not connected'}
            </Typography>
            <Typography
              variant="body3"
              color="text.secondary"
              sx={{ '&:hover': { color: theme.palette.brand[600] } }}
            >
              Learn more
            </Typography>
            <ChevronDownIcon
              strokeWidth={1}
              size={20}
              style={{
                flexShrink: 0,
                transform: readerHelpOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            />
          </Stack>
          <Collapse in={readerHelpOpen}>
            <Divider />
            {/* Both of the panel's actions leave the page. This modal is mounted globally
                (Layout, under ScanRoutingProvider), so it does NOT unmount with the route —
                without this it stayed up over the destination. */}
            <ReaderSetupPanel connected={readerConnected} onNavigate={closeConnect} />
          </Collapse>
        </Stack>

        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          // Static status row — no `interactive`, so no hover tint and no focus outline. Nothing
          // here is clickable, and a hover response on a row that does not respond to a click is
          // the affordance lie §34's two patterns exist to avoid.
          sx={{ px: 4, minHeight: SOURCE_ROW_HEIGHT, ...scanSourceSurfaceSx(theme) }}
        >
          <SmartphoneNfcIcon
            strokeWidth={1}
            size={20}
            color={phoneConnected ? theme.palette.brand[500] : theme.palette.grey[400]}
          />
          <Typography variant="body2" sx={{ flex: 1 }}>
            Phone
          </Typography>
          <Typography
            variant="body3"
            color={phoneConnected ? theme.palette.brand[500] : 'text.secondary'}
          >
            {phoneConnected ? 'Connected' : 'Scan the code below'}
          </Typography>
        </Stack>

        <Divider />

        <Stack spacing={4} alignItems="center" sx={{ minHeight: 218, justifyContent: 'center' }}>
          {registerDeviceURL ? (
            <ScanSessionQRCode registerDeviceURL={registerDeviceURL} />
          ) : (
            <Stack spacing={3} alignItems="center">
              <CircularProgress size={24} />
              <Typography variant="body3" color="text.secondary">
                {starting ? 'Opening a scan session…' : 'No scan session yet.'}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Modal>
  )
}
